import { useEffect, useState } from "react";
import { api, STATUS_STYLES, STATUS_LABELS, fmtTime } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { ShieldStar, SignOut, Plus, FilePdf } from "@phosphor-icons/react";
import { toast } from "sonner";
import { downloadPdf } from "@/pages/ClientsPage";

const emptyIncident = { person_name: "", cell: "", address: "", lat: "", lng: "", description: "", end_client_name: "", requester_name: "", requester_phone: "" };

const Kpi = ({ label, value, testid, accent = "text-white" }) => (
  <div className="border border-zinc-800 bg-[#121214] p-5" data-testid={testid}>
    <div className="text-xs font-bold uppercase tracking-[0.2em] text-zinc-500">{label}</div>
    <div className={`font-heading font-black text-3xl mt-2 font-mono-data ${accent}`}>{value ?? "—"}</div>
  </div>
);

const LivePill = ({ secondsAgo }) => (
  <div
    style={{
      position: "fixed", bottom: 20, right: 24, zIndex: 30,
      padding: "8px 14px", background: "rgba(26,30,36,0.94)", color: "#fff",
      borderRadius: 999, fontSize: 11, fontFamily: "'JetBrains Mono', ui-monospace, monospace",
      letterSpacing: "0.14em", textTransform: "uppercase",
      display: "flex", alignItems: "center", gap: 8,
      boxShadow: "0 8px 24px rgba(0,0,0,0.2)",
      pointerEvents: "none",
    }}
    data-testid="portal-live-indicator"
  >
    <span style={{ width: 7, height: 7, borderRadius: "50%", background: "#1E9E63", boxShadow: "0 0 0 3px rgba(30,158,99,0.28)", animation: "live-pulse 2s ease-in-out infinite" }} />
    LIVE · updated {secondsAgo}s ago
  </div>
);

export default function PortalPage() {
  const { user, logout } = useAuth();
  const [m, setM] = useState(null);
  const [incidents, setIncidents] = useState([]);
  const [form, setForm] = useState(null);
  const [busy, setBusy] = useState(false);
  const [lastPoll, setLastPoll] = useState(Date.now());
  const [, forceTick] = useState(0);

  const load = () => {
    api.get("/portal/metrics").then((r) => setM(r.data));
    api.get("/portal/incidents").then((r) => setIncidents(r.data));
    setLastPoll(Date.now());
  };
  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Live-poll — keeps status chips fresh during an SOS without the client refreshing.
  useEffect(() => {
    const id = setInterval(async () => {
      try {
        const [mm, ii] = await Promise.all([
          api.get("/portal/metrics"),
          api.get("/portal/incidents"),
        ]);
        setM(mm.data);
        setIncidents(ii.data);
        setLastPoll(Date.now());
      } catch (e) { console.error("Portal poll failed (keeping last good data)", e); }
    }, 20000);
    return () => clearInterval(id);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const id = setInterval(() => forceTick((t) => t + 1), 5000);
    return () => clearInterval(id);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const secondsAgo = Math.max(0, Math.floor((Date.now() - lastPoll) / 1000));

  const submitIncident = async () => {
    if (!form.description) return toast.error("Situation description is required");
    setBusy(true);
    try {
      const payload = { ...form };
      payload.lat = payload.lat === "" ? null : parseFloat(payload.lat);
      payload.lng = payload.lng === "" ? null : parseFloat(payload.lng);
      Object.keys(payload).forEach((k) => { if (payload[k] === "") payload[k] = null; });
      const r = await api.post("/portal/incidents", payload);
      toast.success(`Incident logged — ${r.data.ref}`, { description: "ResQconnect dispatch has been notified." });
      setForm(null);
      load();
    } catch (e) {
      toast.error("Failed to log incident", { description: e.response?.data?.detail || e.message });
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#09090b] text-white" data-testid="portal-page">
      <header className="flex items-center justify-between px-8 py-4 border-b border-zinc-800 bg-zinc-900/90 backdrop-blur sticky top-0 z-10">
        <div className="flex items-center gap-2">
          <ShieldStar size={26} weight="duotone" className="text-[#007AFF]" />
          <div>
            <div className="font-heading font-black tracking-tighter text-lg leading-none">RESQCONNECT</div>
            <div className="text-[10px] uppercase tracking-[0.25em] text-zinc-500">Client Portal — {user?.name}</div>
          </div>
        </div>
        <button onClick={logout} className="btn-tactical bg-zinc-800 hover:bg-zinc-700 text-white flex items-center gap-2" data-testid="portal-logout-button">
          <SignOut size={14} weight="bold" /> Sign Out
        </button>
      </header>

      <div className="p-8">
        <h1 className="font-heading text-4xl font-black tracking-tighter uppercase">Your Dispatch Activity</h1>
        <p className="text-zinc-400 text-sm mt-1">Live view of every incident logged for {user?.name}, with response performance.</p>

        <div className="grid grid-cols-2 lg:grid-cols-6 gap-px bg-zinc-800 border border-zinc-800 mt-8">
          <Kpi label="Total Incidents" value={m?.total} testid="portal-kpi-total" />
          <Kpi label="Open" value={m?.open} testid="portal-kpi-open" accent="text-yellow-400" />
          <Kpi label="Closed" value={m?.closed} testid="portal-kpi-closed" accent="text-green-400" />
          <Kpi label="Avg Dispatch (min)" value={m?.avg_dispatch_minutes} testid="portal-kpi-dispatch" accent="text-[#007AFF]" />
          <Kpi label="Avg Resolution (min)" value={m?.avg_resolution_minutes} testid="portal-kpi-resolution" />
          <Kpi label="SLA Compliance" value={m?.sla_compliance_pct != null ? `${m.sla_compliance_pct}%` : "—"} testid="portal-kpi-sla" accent={m?.sla_breached ? "text-red-400" : "text-green-400"} />
        </div>

        <div className="mt-8 flex flex-wrap gap-2 items-start">
          <button onClick={() => downloadPdf("/portal/report", "resqconnect-performance-report.pdf").then(() => toast.success("Report downloaded")).catch(() => toast.error("Report download failed"))} className="btn-tactical bg-zinc-800 hover:bg-zinc-700 text-white flex items-center gap-2" data-testid="portal-report-button">
            <FilePdf size={14} weight="bold" /> Download Performance Report (PDF)
          </button>
          {!form ? (
            <button onClick={() => setForm({ ...emptyIncident })} className="btn-tactical bg-[#FF3B30] hover:bg-[#d32f27] text-white flex items-center gap-2" data-testid="portal-new-incident-button">
              <Plus size={14} weight="bold" /> Log New Incident
            </button>
          ) : (
            <div className="w-full border-2 border-orange-500/50 bg-[#1a120b] p-6 grid md:grid-cols-2 gap-4" data-testid="portal-incident-form">
              <div className="md:col-span-2 -mt-1">
                <span className="text-xs font-bold uppercase tracking-[0.25em] text-orange-400">New Incident — complete and submit for immediate dispatch</span>
              </div>
              <div>
                <label className="text-xs font-bold uppercase tracking-[0.2em] text-zinc-500">Your Name (requester)</label>
                <input className="tactical-input mt-1" value={form.requester_name} onChange={(e) => setForm({ ...form, requester_name: e.target.value })} data-testid="portal-requester-name-input" />
              </div>
              <div>
                <label className="text-xs font-bold uppercase tracking-[0.2em] text-zinc-500">Your Tel (requester)</label>
                <input className="tactical-input mt-1 font-mono-data text-xs" value={form.requester_phone} onChange={(e) => setForm({ ...form, requester_phone: e.target.value })} data-testid="portal-requester-phone-input" />
              </div>
              <div>
                <label className="text-xs font-bold uppercase tracking-[0.2em] text-zinc-500">Person Name</label>
                <input className="tactical-input mt-1" value={form.person_name} onChange={(e) => setForm({ ...form, person_name: e.target.value })} data-testid="portal-person-input" />
              </div>
              <div>
                <label className="text-xs font-bold uppercase tracking-[0.2em] text-zinc-500">Cell Number</label>
                <input className="tactical-input mt-1 font-mono-data text-xs" value={form.cell} onChange={(e) => setForm({ ...form, cell: e.target.value })} data-testid="portal-cell-input" />
              </div>
              <div>
                <label className="text-xs font-bold uppercase tracking-[0.2em] text-zinc-500">Google Maps Link or Coordinates <span className="text-zinc-600 normal-case tracking-normal">(paste — auto-fills below)</span></label>
                <input className="tactical-input mt-1 font-mono-data text-xs" placeholder="https://maps.app.goo.gl/... or -26.2041, 28.0473"
                  value={form.gpaste || ""}
                  onChange={(e) => setForm({ ...form, gpaste: e.target.value })}
                  onPaste={async (e) => {
                    const text = e.clipboardData.getData("text");
                    if (!text) return;
                    setForm((f) => ({ ...f, gpaste: text }));
                    try {
                      const r = await api.post("/geocode/paste", { text });
                      if (r.data.lat != null && r.data.lng != null) {
                        setForm((f) => ({ ...f, lat: r.data.lat, lng: r.data.lng, address: f.address || r.data.address || f.address }));
                        toast.success("Location extracted", { description: `${r.data.lat}, ${r.data.lng}${r.data.address ? " · " + r.data.address.slice(0, 60) : ""}` });
                      } else {
                        toast.error("Could not read a location from that paste", { description: "Try the coordinates or a Google Maps link" });
                      }
                    } catch (err) {
                      toast.error("Location lookup failed", { description: err.response?.data?.detail || err.message });
                    }
                  }}
                  data-testid="portal-gpaste-input" />
              </div>
              <div>
                <label className="text-xs font-bold uppercase tracking-[0.2em] text-zinc-500">Address</label>
                <input className="tactical-input mt-1" value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} data-testid="portal-address-input" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs font-bold uppercase tracking-[0.2em] text-zinc-500">Lat</label>
                  <input className="tactical-input mt-1 font-mono-data text-xs" value={form.lat} onChange={(e) => setForm({ ...form, lat: e.target.value })} data-testid="portal-lat-input" />
                </div>
                <div>
                  <label className="text-xs font-bold uppercase tracking-[0.2em] text-zinc-500">Lng</label>
                  <input className="tactical-input mt-1 font-mono-data text-xs" value={form.lng} onChange={(e) => setForm({ ...form, lng: e.target.value })} data-testid="portal-lng-input" />
                </div>
              </div>
              <div className="md:col-span-2">
                <label className="text-xs font-bold uppercase tracking-[0.2em] text-zinc-500">Situation Description *</label>
                <textarea className="tactical-input mt-1 min-h-[90px]" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="portal-description-input" />
              </div>
              <div className="flex gap-2 md:col-span-2">
                <button onClick={submitIncident} disabled={busy} className="btn-tactical bg-[#FF3B30] hover:bg-[#d32f27] text-white" data-testid="portal-submit-incident-button">
                  {busy ? "Logging..." : "Submit Incident"}
                </button>
                <button onClick={() => setForm(null)} className="btn-tactical bg-zinc-800 hover:bg-zinc-700 text-white">Cancel</button>
              </div>
            </div>
          )}
        </div>


        <div className="border border-zinc-800 bg-[#121214] mt-8">
          <div className="px-6 py-4 border-b border-zinc-800 text-xs font-bold uppercase tracking-[0.2em] text-zinc-500">
            Incident History
          </div>
          {incidents.length === 0 && (
            <div className="text-zinc-600 text-sm font-mono-data p-8 text-center">NO INCIDENTS ON RECORD</div>
          )}
          {incidents.map((i) => (
            <div key={i.id} className="px-6 py-4 border-b border-zinc-800 last:border-0" data-testid={`portal-incident-${i.ref}`}>
              <div className="flex flex-wrap items-center justify-between gap-2">
                <div className="flex items-center gap-3">
                  <span className="font-mono-data text-sm">{i.ref}</span>
                  {i.csg_incident_no && <span className="font-mono-data text-xs text-[#007AFF]">CSG {i.csg_incident_no}</span>}
                  <span className={`text-[10px] font-bold px-2 py-0.5 border rounded-sm ${STATUS_STYLES[i.status]}`}>
                    {STATUS_LABELS[i.status]}
                  </span>
                </div>
                <div className="font-mono-data text-[11px] text-zinc-500">
                  Received {fmtTime(i.received_at)}
                  {i.dispatched_at && ` · Dispatched ${fmtTime(i.dispatched_at)}`}
                  {i.closed_at && ` · Closed ${fmtTime(i.closed_at)}`}
                </div>
              </div>
              <div className="text-sm text-zinc-300 mt-1.5">{i.description || i.subject}</div>
              <div className="font-mono-data text-xs text-zinc-500 mt-1">
                {i.person_name || "—"} · {i.cell || "—"} · {i.address || (i.lat != null ? `${i.lat}, ${i.lng}` : "—")}
              </div>
            </div>
          ))}
        </div>
      </div>
      <LivePill secondsAgo={secondsAgo} />
    </div>
  );
}
